import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { TestModuleService } from '../../../services/test-module.service';
import { TestModule } from '../../../models/test-module.model';

/**
 * Inline-Panel zum Einbinden eines Testmoduls im Modul-Edit-Drawer (NEX-690). Klappt
 * unter dem Button „Testmodul einbinden" auf, listet projektweit Module (aktuelles und
 * bereits eingebundene über {@link excludeIds} ausgeschlossen) und meldet die Auswahl
 * via {@link applied}. Der separate {@code app-module-embed-dialog} bleibt für die
 * Testfall-Anlage; im Drawer wird bewusst dieses Inline-Panel genutzt.
 */
@Component({
  selector: 'app-module-embed-inline',
  imports: [TranslatePipe, MatIcon],
  templateUrl: './module-embed-inline.component.html',
  styleUrl: './module-embed-inline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleEmbedInlineComponent implements OnInit {
  private readonly moduleService = inject(TestModuleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  /** IDs, die nicht wählbar sind: das aktuelle Modul + bereits im Draft eingebundene. */
  readonly excludeIds = input<readonly string[]>([]);

  readonly applied = output<TestModule>();
  readonly closed = output<void>();

  private readonly modules = signal<TestModule[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly searchTerm = signal('');

  readonly candidates = computed(() => {
    const exclude = new Set(this.excludeIds());
    const term = this.searchTerm().toLowerCase().trim();
    return this.modules()
      .filter((m) => !exclude.has(m.id))
      .filter((m) => !term || m.name.toLowerCase().includes(term) || m.key.toLowerCase().includes(term));
  });

  ngOnInit(): void {
    this.moduleService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (modules) => {
          this.modules.set(modules);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  onSearch(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  select(module: TestModule): void {
    this.applied.emit(module);
  }

  close(): void {
    this.closed.emit();
  }
}
